import { adminApi } from '@/lib/axios';

const DEFAULT_MAX_PER_MINUTE = 10;
const DEFAULT_MAX_TITLE = 140;
const DEFAULT_MAX_DETAILS = 3500;

function nowIso() {
  return new Date().toISOString();
}

function safeJson(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function truncate(str, max) {
  const s = String(str ?? '');
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

function normalizeErrorLike(err) {
  if (!err) return { message: 'Unknown error' };
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: err.stack,
    };
  }
  // axios-ish / thrown objects
  const message =
    err?.message ||
    err?.toString?.() ||
    (typeof err === 'string' ? err : 'Unknown error');
  return {
    name: err?.name,
    message: String(message),
    stack: err?.stack,
    raw: safeJson(err),
  };
}

function getRateState() {
  const key = '__shopitrade_error_reporter_rate_v1__';
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object') return { key, windowStart: Date.now(), count: 0 };
    return { key, ...parsed };
  } catch {
    return { key, windowStart: Date.now(), count: 0 };
  }
}

function setRateState(state) {
  try {
    window.localStorage.setItem(
      state.key,
      JSON.stringify({ windowStart: state.windowStart, count: state.count })
    );
  } catch {
    // ignore
  }
}

function allowSend(maxPerMinute) {
  const ms = 60_000;
  const state = getRateState();
  const now = Date.now();
  if (now - state.windowStart > ms) {
    const next = { ...state, windowStart: now, count: 1 };
    setRateState(next);
    return true;
  }
  if (state.count >= maxPerMinute) return false;
  const next = { ...state, count: state.count + 1 };
  setRateState(next);
  return true;
}

export async function reportErrorToWebhook(payload) {
  const url = process.env.REACT_APP_ERROR_WEBHOOK_URL;
  if (!url) return;
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // ignore - never crash app on reporting
  }
}

export async function reportErrorToAdminSlack({ title, details }) {
  const enabled =
    String(process.env.REACT_APP_ERROR_TO_SLACK || '').toLowerCase() === 'true' ||
    String(process.env.REACT_APP_ERROR_TO_SLACK || '') === '1';
  if (!enabled) return;

  try {
    // Endpoint: {ADMIN_API_URL}/utilities/utilities/SendMessageToSlack
    // Controller signature is SendMessageToSlack(string title, string details)
    await adminApi.post('/utilities/SendMessageToSlack', null, {
      params: {
        title: truncate(title, DEFAULT_MAX_TITLE),
        details: truncate(details, DEFAULT_MAX_DETAILS),
      },
    });
  } catch {
    // ignore - never crash app on reporting
  }
}

export function createErrorPayload({ error, source, extra }) {
  const normalized = normalizeErrorLike(error);
  return {
    timestamp: nowIso(),
    source: source || 'unknown',
    href: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    error: normalized,
    extra: extra || undefined,
  };
}

export async function reportError({ error, source, extra, maxPerMinute = DEFAULT_MAX_PER_MINUTE }) {
  // Store last errors locally so we can recover even if webhook isn’t configured.
  try {
    const key = '__shopitrade_recent_errors_v1__';
    const prev = JSON.parse(window.localStorage.getItem(key) || '[]');
    const next = Array.isArray(prev) ? prev.slice(-49) : [];
    next.push(createErrorPayload({ error, source, extra }));
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore
  }

  if (!allowSend(maxPerMinute)) return;
  const payload = createErrorPayload({ error, source, extra });
  const title = `[admin] ${payload.source}: ${payload.error?.message || 'error'}`;
  const details = safeJson(payload);
  await Promise.all([
    reportErrorToAdminSlack({ title, details }),
    reportErrorToWebhook(payload),
  ]);
}

export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  if (window.__shopitradeErrorHandlersInstalled) return;
  window.__shopitradeErrorHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    void reportError({
      source: 'window.error',
      error: event?.error || new Error(event?.message || 'window.error'),
      extra: {
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    void reportError({
      source: 'window.unhandledrejection',
      error: event?.reason || new Error('unhandledrejection'),
    });
  });
}

